// Custom /callback handler: oidc-client-ts token exchange omits Serial PKCE, so AuthProvider sets skipSigninCallback.
import { log } from "@/config/logging";
import FullScreenLoading from "@/components/FullScreenLoading";
import { AUTH_REDIRECT_URI_STORAGE_KEY } from "@/constants";
import { userManager } from "@/provider/AuthProvider/userManager";
import {
  forgetRememberedAccounts,
  readRememberedAccounts,
} from "@/store/rememberedAccountsStorage";
import {
  exchangeTrimbleAuthorizationCode,
  isAuthCallbackPath,
} from "@/utils/silentTokenRefresh";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import * as z from "zod";

const URL_STATE_DELIMITER = ";";

const signinStateSchema = z.object({
  code_verifier: z.string(),
});

function parseCallbackStateId(stateParam: string): string | undefined {
  const splitState = stateParam.split(URL_STATE_DELIMITER);
  const stateId = splitState[0];
  if (stateId === undefined || stateId.length === 0) {
    return undefined;
  }
  return stateId;
}

// Login code_verifier lives in UserManager state store; silentTokenRefresh avoids importing userManager (circular deps).
async function readOidcSigninCodeVerifier(
  url: string
): Promise<{ stateId: string; codeVerifier: string }> {
  const stateParam = new URLSearchParams(new URL(url).search).get("state");
  if (stateParam === null) {
    throw new Error("Missing state in callback URL");
  }

  const stateId = parseCallbackStateId(stateParam);
  if (stateId === undefined) {
    throw new Error("Missing state in callback URL");
  }

  const stored = await userManager.settings.stateStore.get(stateId);
  if (stored === null) {
    throw new Error(
      `No matching OIDC sign-in state found for state "${stateId}"`
    );
  }

  const parsed: unknown = JSON.parse(stored);
  const signinState = signinStateSchema.parse(parsed);
  return { stateId, codeVerifier: signinState.code_verifier };
}

async function clearOidcSigninState(stateId: string): Promise<void> {
  await userManager.settings.stateStore.remove(stateId);
}

function getPathWithoutQuery(path: string): string {
  const queryIndex = path.indexOf("?");
  return queryIndex === -1 ? path : path.slice(0, queryIndex);
}

function getPostLoginRedirectUrl(): string {
  const stored = sessionStorage.getItem(AUTH_REDIRECT_URI_STORAGE_KEY);
  if (stored !== null && stored.length > 0) {
    const pathWithoutQuery = getPathWithoutQuery(stored);
    if (!isAuthCallbackPath(pathWithoutQuery)) {
      return stored;
    }
  }
  return "/";
}

async function runAuthCallback(
  navigate: ReturnType<typeof useNavigate>
): Promise<void> {
  const callbackUrl = window.location.href;
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (error !== null) {
    // A stale account_id hint can wedge sign-in, so drop it and reload to rebuild
    // ProtectedRoute's args rather than retrying with the same hint.
    if (readRememberedAccounts() !== null) {
      forgetRememberedAccounts();
      window.location.replace("/");
      return;
    }
    throw new Error(params.get("error_description") ?? error);
  }

  const { stateId, codeVerifier } =
    await readOidcSigninCodeVerifier(callbackUrl);
  const user = await exchangeTrimbleAuthorizationCode(
    callbackUrl,
    codeVerifier
  );

  await userManager.storeUser(user);
  await userManager.events.load(user);
  await clearOidcSigninState(stateId);

  const redirectPath = getPostLoginRedirectUrl();
  sessionStorage.removeItem(AUTH_REDIRECT_URI_STORAGE_KEY);
  window.history.replaceState({}, document.title, window.location.pathname);
  navigate(redirectPath, { replace: true });
}

const AuthCallback = () => {
  const navigate = useNavigate();
  const callbackInFlightRef = useRef(false);

  useEffect(() => {
    if (callbackInFlightRef.current) {
      return;
    }

    callbackInFlightRef.current = true;

    const completeCallback = async (): Promise<void> => {
      try {
        await runAuthCallback(navigate);
      } catch (error: unknown) {
        log.error(error, { message: "Trimble sign-in callback failed" });
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate("/", { replace: true });
      }
    };

    void completeCallback();
  }, [navigate]);

  return <FullScreenLoading />;
};

export default AuthCallback;
